
import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    TextField,
    IconButton,
    Paper,
    Avatar,
    Divider,
    Button,
    Tooltip,
    CircularProgress,
    Stack
} from '@mui/material';
import {
    Send as SendIcon,
    AttachFile as AttachFileIcon,
    Image as ImageIcon,
    Delete as DeleteIcon,
    CloudUpload as CloudUploadIcon,
    InsertDriveFile as FileIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import moment from 'moment';
import Cookies from 'js-cookie';
import api from '../../../../../../services/api';

// Auxiliar para carregar mídias autenticadas
// Auxiliar para carregar mídias autenticadas
const AttachmentMedia = ({ caminho, darkMode }) => {
    const [src, setSrc] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    // Constrói URL direta via proxy
    const urlDireta = caminho ? caminho.replace('/home/wwgrup/rondamotorizada/', 'https://gruporosinetos.com/rmmidias/') : '';

    useEffect(() => {
        let isCancelled = false;
        let objectUrl = null;

        const loadMedia = async () => {
            if (!caminho) return;
            try {
                // Tenta buscar via API (autenticado)
                const response = await api.get(
                    `v1/protected/rondamotorizada/comentario/midia?caminho=${encodeURIComponent(caminho)}`,
                    { responseType: 'blob' }
                );
                
                if (!isCancelled) {
                    objectUrl = URL.createObjectURL(response.data);
                    setSrc(objectUrl);
                    setLoading(false);
                }
            } catch (err) {
                console.error("Erro ao carregar mídia via API, tentando direta:", err);
                if (!isCancelled) {
                    setSrc(urlDireta);
                    setLoading(false);
                }
            }
        };

        loadMedia();

        return () => {
            isCancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [caminho, urlDireta]);

    if (loading) return <Typography variant="caption" sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}><CircularProgress size={12} /> Carregando mídia...</Typography>;
    if (error) return <Typography variant="caption" color="error" sx={{ p: 1, display: 'block' }}>❌ Erro no anexo</Typography>;

    const pathLower = caminho?.toLowerCase() || '';
    const isPdf = pathLower.endsWith('.pdf');
    const isVideo = pathLower.endsWith('.mp4') || pathLower.endsWith('.mov') || pathLower.endsWith('.avi') || pathLower.endsWith('.webm');
    const isAudio = pathLower.endsWith('.m4a') || pathLower.endsWith('.wav') || pathLower.endsWith('.mp3') || pathLower.endsWith('.aac') || pathLower.endsWith('.ogg');

    if (isPdf) {
        return (
            <Button 
                variant="outlined" 
                size="small" 
                startIcon={<FileIcon />}
                fullWidth
                onClick={() => window.open(src || urlDireta, '_blank')}
                sx={{ textTransform: 'none', fontSize: '0.7rem', mt: 1, borderRadius: 2 }}
            >
                Visualizar PDF
            </Button>
        );
    }

    if (isVideo) {
        return (
            <Box sx={{ mt: 1, width: '100%', borderRadius: 2, overflow: 'hidden', border: darkMode ? '1px solid #444' : '1px solid #eee' }}>
                <video 
                    controls 
                    style={{ width: '100%', display: 'block', maxHeight: '300px', backgroundColor: '#000' }}
                    src={src || urlDireta}
                >
                    Seu navegador não suporta a tag de vídeo.
                </video>
            </Box>
        );
    }

    if (isAudio) {
        return (
            <Box sx={{ mt: 1, width: '100%', p: 1, bgcolor: darkMode ? 'rgba(33, 150, 243, 0.1)' : 'rgba(33, 150, 243, 0.05)', borderRadius: 2, border: '1px solid rgba(33, 150, 243, 0.2)' }}>
                 <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 'bold', color: 'primary.main' }}>
                    MENSAGEM DE VOZ
                </Typography>
                <audio 
                    controls 
                    style={{ width: '100%', height: '32px' }}
                    src={src || urlDireta}
                >
                    Seu navegador não suporta a tag de áudio.
                </audio>
            </Box>
        );
    }

    return (
        <Box 
            component="img"
            src={src || urlDireta} 
            alt="Anexo" 
            onError={() => setError(true)}
            onClick={() => window.open(src || urlDireta, '_blank')}
            sx={{ 
                mt: 1, 
                maxWidth: '100%', 
                display: 'block', 
                cursor: 'pointer', 
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: darkMode ? '1px solid #444' : '1px solid #eee',
                '&:hover': { opacity: 0.9, transform: 'scale(1.01)' },
                transition: 'all 0.2s'
            }}
        />
    );
};

const LogInterativoRonda = ({ rondaId, darkMode, themeHelper }) => {
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [files, setFiles] = useState([]);
    const scrollRef = useRef(null);

    const fetchComments = async () => {
        try {
            const response = await api.get(`v1/protected/rondamotorizada/comentario/listar/${rondaId}`);
            setComments(Array.isArray(response.data) ? response.data : []);

            setLoading(false);
        } catch (err) {
            console.error('Erro ao buscar comentários:', err);
            setLoading(false);
        }
    };

    useEffect(() => {
        if (rondaId) {
            fetchComments();
            // Polling a cada 10 segundos
            const timer = setInterval(fetchComments, 10000);
            return () => clearInterval(timer);
        }
    }, [rondaId]);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [comments]);

    const onDrop = (acceptedFiles) => {
        setFiles([...files, ...acceptedFiles]);
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'image/*': [],
            'application/pdf': [],
            'video/*': [],
            'audio/*': []
        }
    });

    const removeFile = (index) => {
        const newFiles = [...files];
        newFiles.splice(index, 1);
        setFiles(newFiles);
    };

    const handleSendMessage = async () => {
        if (!message.trim() && files.length === 0) return;

        setSending(true);
        try {
            const userId = Cookies.get('id_usuario');
            
            if (files.length > 0) {
                for (let i = 0; i < files.length; i++) {
                    const formData = new FormData();
                    formData.append('id_ronda_motorizada', rondaId);
                    formData.append('id_usuario', userId);
                    formData.append('mensagem', i === 0 ? message : `Arquivo anexo ${i + 1}`);
                    formData.append('foto', files[i]);

                    await api.post('v1/protected/rondamotorizada/comentario/gravar', formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    });
                }
            } else {
                const formData = new FormData();
                formData.append('id_ronda_motorizada', rondaId);
                formData.append('id_usuario', userId);
                formData.append('mensagem', message);

                await api.post('v1/protected/rondamotorizada/comentario/gravar', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }

            setMessage('');
            setFiles([]);
            fetchComments();
        } catch (err) {
            console.error('Erro ao enviar mensagem:', err);
            alert('Erro ao enviar mensagem. Tente novamente.');
        } finally {
            setSending(false);
        }
    };

    return (
        <Paper variant="outlined" sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: darkMode ? '#1e1e1e' : '#fff',
            borderColor: darkMode ? '#333' : '#eee',
            borderRadius: 3,
            overflow: 'hidden'
        }}>
            <Box sx={{ p: 2, borderBottom: `1px solid ${darkMode ? '#333' : '#eee'}`, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CloudUploadIcon color="primary" />
                <Typography variant="subtitle1" fontWeight="bold" sx={{ color: darkMode ? '#fff' : 'inherit' }}>Logs e Auditoria Interativa</Typography>
            </Box>

            <Box ref={scrollRef} sx={{ flexGrow: 1, p: 2, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2, bgcolor: darkMode ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.02)', maxHeight: '500px' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={24} /></Box>
                ) : comments.length === 0 ? (
                    <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}>Nenhuma interação registrada ainda.</Typography>
                ) : (
                    comments.slice().reverse().map((comment, index) => (
                        <Box key={index} sx={{ alignSelf: 'flex-start', maxWidth: '85%' }}>
                            <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                                <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem', bgcolor: 'primary.main' }}>
                                    {comment.nome_amigavel?.charAt(0) || 'U'}
                                </Avatar>
                                <Typography variant="caption" sx={{ fontWeight: 'bold', color: darkMode ? '#aaa' : '#666' }}>
                                    {comment.nome_amigavel || 'Usuário'} • {moment(comment.data_hora).format('HH:mm DD/MM')}
                                </Typography>
                            </Box>
                            <Box sx={{ 
                                p: 1.5, 
                                bgcolor: darkMode ? '#2d2d2d' : '#fff', 
                                color: darkMode ? '#eee' : '#333',
                                borderRadius: '0 12px 12px 12px',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                border: darkMode ? '1px solid #444' : '1px solid #eee'
                            }}>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{comment.mensagem}</Typography>
                                {comment.caminho_foto && (
                                    <AttachmentMedia caminho={comment.caminho_foto} darkMode={darkMode} />
                                )}
                            </Box>
                        </Box>
                    ))
                )}
            </Box>

            <Box sx={{ p: 2, borderTop: `1px solid ${darkMode ? '#333' : '#eee'}`, bgcolor: darkMode ? '#1e1e1e' : '#fff' }}>
                {files.length > 0 && (
                    <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                        {files.map((file, i) => (
                            <Box key={i} sx={{ position: 'relative', p: 1, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ImageIcon fontSize="small" color="primary" />
                                <Typography variant="caption" sx={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</Typography>
                                <IconButton size="small" onClick={() => removeFile(i)}><CloseIcon sx={{ fontSize: 14 }} /></IconButton>
                            </Box>
                        ))}
                    </Stack>
                )}

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <div {...getRootProps()}>
                        <input {...getInputProps()} />
                        <Tooltip title="Anexar arquivo/foto">
                            <IconButton color="primary" disabled={sending}><AttachFileIcon /></IconButton>
                        </Tooltip>
                    </div>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Escreva uma mensagem ou log..."
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        disabled={sending}
                        onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                        sx={{ 
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 5,
                                bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : '#f9f9f9',
                                color: darkMode ? '#fff' : 'inherit'
                            }
                        }}
                    />
                    <IconButton 
                        color="primary" 
                        onClick={handleSendMessage} 
                        disabled={sending || (!message.trim() && files.length === 0)}
                        sx={{ bgcolor: 'primary.main', color: '#fff', '&:hover': { bgcolor: 'primary.dark' } }}
                    >
                        {sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                    </IconButton>
                </Box>
            </Box>
        </Paper>
    );
};

export default LogInterativoRonda;
